import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminSpin, updateAdminSpin } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Save, Plus, Trash2, RotateCw } from 'lucide-react';

export default function AdminSpinWheel() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ enabled: true, frequency: 'daily', segments: [], coin_rate_gbp: 1, coin_name: 'Luxorcoin' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadConfig();
  }, [user, isAdmin, navigate, authLoading]);

  const loadConfig = async () => {
    try {
      const res = await getAdminSpin();
      const cfg = res.data.spin_wheel || { enabled: true, frequency: 'daily', segments: [] };
      setConfig({
        enabled: cfg.enabled !== false,
        frequency: cfg.frequency || 'daily',
        segments: cfg.segments || [],
        coin_rate_gbp: cfg.coin_rate_gbp || 1,
        coin_name: cfg.coin_name || 'Luxorcoin',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateSegment = (idx, field, value) => {
    setConfig(prev => ({
      ...prev,
      segments: prev.segments.map((s, i) => i === idx ? { ...s, [field]: field === 'value' || field === 'weight' ? parseInt(value) || 0 : value } : s)
    }));
  };

  const addSegment = () => {
    setConfig(prev => ({
      ...prev,
      segments: [...prev.segments, { value: 5, weight: 10, color: '#D4AF37' }]
    }));
  };

  const removeSegment = (idx) => {
    setConfig(prev => ({ ...prev, segments: prev.segments.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    try {
      await updateAdminSpin(config);
      toast.success(t('spinSaved'));
    } catch { toast.error(t('toastFailed')); }
  };

  if (authLoading || !user || !isAdmin) return null;

  const totalWeight = config.segments.reduce((s, seg) => s + (seg.weight || 0), 0);

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{t('spinWheel')}</h1>
            <p className="text-muted-foreground mt-1 text-sm">{t('manageSpinDesc')}</p>
          </div>
          <Button className="bg-primary text-primary-foreground btn-press gap-2 rounded-xl" onClick={handleSave}><Save className="w-4 h-4" /> {t('saveAll')}</Button>
        </div>

        {loading ? <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Settings */}
            <Card className="bg-white border-border">
              <CardHeader><CardTitle className="text-lg">{t('settings')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>{t('enabled')}</Label>
                  <Switch checked={config.enabled} onCheckedChange={(v) => setConfig({...config, enabled: v})} />
                </div>
                <div className="space-y-2">
                  <Label>{t('frequency')}</Label>
                  <Select value={config.frequency} onValueChange={(v) => setConfig({...config, frequency: v})}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12h">{t('freq12h')}</SelectItem>
                      <SelectItem value="daily">{t('freqDaily')}</SelectItem>
                      <SelectItem value="2days">{t('freq2days')}</SelectItem>
                      <SelectItem value="3days">{t('freq3days')}</SelectItem>
                      <SelectItem value="weekly">{t('freqWeekly')}</SelectItem>
                      <SelectItem value="biweekly">{t('freqBiweekly')}</SelectItem>
                      <SelectItem value="monthly">{t('freqMonthly')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Luxorcoin configuration */}
                <div className="pt-3 border-t border-border space-y-3">
                  <p className="text-xs uppercase tracking-wider font-bold text-gold-dark">🪙 Configuration Luxorcoins</p>
                  <div className="space-y-2">
                    <Label className="text-sm">Nom de la monnaie virtuelle</Label>
                    <Input
                      value={config.coin_name || 'Luxorcoin'}
                      onChange={(e) => setConfig({ ...config, coin_name: e.target.value })}
                      placeholder="Luxorcoin"
                      className="rounded-xl"
                    />
                    <p className="text-[11px] text-muted-foreground">Affiché à l'écran pendant le tirage (ex: "Luxorcoin", "WL Token", "Star")</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Taux de conversion (£ par Luxorcoin)</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-rolex-green/65 font-mono text-sm">1 {config.coin_name || 'Luxorcoin'} =</span>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={config.coin_rate_gbp || 1}
                        onChange={(e) => setConfig({ ...config, coin_rate_gbp: parseFloat(e.target.value) || 0 })}
                        className="w-28 rounded-xl font-mono text-center"
                      />
                      <span className="text-rolex-green/65 font-mono text-sm">£ crédit</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      💡 Ex: si vous mettez <code>1</code>, gagner 5 Luxorcoins = +£5 de crédit. Si vous mettez <code>0.5</code>, gagner 10 Luxorcoins = +£5 de crédit.
                    </p>
                  </div>
                </div>
                <div className="pt-3 border-t border-border text-sm text-muted-foreground">
                  <p>{t('totalSegments')}: <span className="font-mono font-bold text-foreground">{config.segments.length}</span></p>
                  <p>{t('totalWeightLabel')}: <span className="font-mono font-bold text-foreground">{totalWeight}</span></p>
                </div>
              </CardContent>
            </Card>

            {/* Segments */}
            <Card className="bg-white border-border lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{t('prizes')}</CardTitle>
                  <Button size="sm" variant="outline" className="gap-1 rounded-xl" onClick={addSegment}><Plus className="w-3 h-3" /> {t('addPrize')}</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-muted-foreground px-1">
                    <span className="col-span-2">{t('points')}</span>
                    <span className="col-span-2">{t('weight')}</span>
                    <span className="col-span-3">{t('probability')}</span>
                    <span className="col-span-3">{t('color')}</span>
                    <span className="col-span-2"></span>
                  </div>
                  {config.segments.map((seg, idx) => {
                    const prob = totalWeight > 0 ? ((seg.weight / totalWeight) * 100).toFixed(1) : 0;
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-2">
                          <Input type="number" value={seg.value} onChange={(e) => updateSegment(idx, 'value', e.target.value)} className="rounded-lg text-sm font-mono" />
                        </div>
                        <div className="col-span-2">
                          <Input type="number" value={seg.weight} onChange={(e) => updateSegment(idx, 'weight', e.target.value)} className="rounded-lg text-sm font-mono" />
                        </div>
                        <div className="col-span-3 flex items-center gap-2">
                          <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{width: `${prob}%`}} />
                          </div>
                          <span className="font-mono text-xs text-muted-foreground w-12">{prob}%</span>
                        </div>
                        <div className="col-span-3 flex items-center gap-2">
                          <input type="color" value={seg.color || '#D4AF37'} onChange={(e) => updateSegment(idx, 'color', e.target.value)} className="w-8 h-8 rounded border-0 cursor-pointer" />
                          <span className="font-mono text-xs text-muted-foreground">{seg.color}</span>
                        </div>
                        <div className="col-span-2 text-right">
                          <Button size="icon" variant="ghost" className="text-red-400" onClick={() => removeSegment(idx)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {config.segments.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">{t('noSegments')}</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
